/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import logger from './logger';

/**
 * The pair of applications a visitor picked on the login page, held until they
 * land on the prebuilt integrations catalogue after signing in.
 *
 * This rides alongside the Explore integrations intent next door in
 * `exploreIntegrationsIntent.ts` rather than inside it: that one is *consumed*
 * by `CreateDefaultEnvs` to decide where a brand new organization lands, one
 * page before the catalogue is mounted, so a selection stored in it would be
 * gone by the time the catalogue could read it. Kept separate, the catalogue
 * consumes this on its own, whichever of the two routes brought it there.
 *
 * A pair rather than one integration: the chip the visitor pressed stands for
 * every published integration connecting those two applications, so the choice
 * this carries is the pair, and the catalogue opens on its own list of the
 * integrations between them for the visitor to pick from. It is also resolved
 * against the catalogue the signed in product fetches rather than trusting a
 * copy of it from before sign in.
 *
 * Stored the same way, and for the same reasons, as the pending Copilot prompt
 * in `copilotPrompt.ts` — see the note there on why it is `localStorage`.
 */
const PENDING_KEY = 'devant_pending_prebuilt_integration';

/** An older selection is ignored — the sign up was abandoned. */
const TTL_MS = 30 * 60 * 1000;

interface StoredSelection {
  applications: string[];
  createdAt: number;
}

const isApplicationPair = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((application) => typeof application === 'string' && application);

/**
 * Records the picked pair for the sign in round trip, replacing any previous
 * one.
 *
 * Every access to `localStorage` is guarded: it throws outright in Safari
 * private mode and wherever the browser is set to block site data, and losing
 * the selection must never break signing in.
 */
export const setPendingPrebuiltIntegration = (applications: string[]) => {
  if (!isApplicationPair(applications)) {
    return;
  }
  try {
    const entry: StoredSelection = { applications, createdAt: Date.now() };
    localStorage.setItem(PENDING_KEY, JSON.stringify(entry));
  } catch (error) {
    logger.error('Failed to store the pending prebuilt integration', error);
  }
};

export const clearPendingPrebuiltIntegration = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (error) {
    logger.error('Failed to clear the pending prebuilt integration', error);
  }
};

/**
 * The stored pair, or `null` when there is none, it has expired or it cannot
 * be read. An expired entry is removed on the way out.
 */
export const getPendingPrebuiltIntegration = (): string[] | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_KEY);
  } catch (error) {
    logger.error('Failed to read the pending prebuilt integration', error);
    return null;
  }
  if (!raw) {
    return null;
  }

  let entry: StoredSelection;
  try {
    entry = JSON.parse(raw);
  } catch (error) {
    // Corrupt or from an older format — drop it rather than keep failing.
    clearPendingPrebuiltIntegration();
    return null;
  }

  if (
    !entry ||
    !isApplicationPair(entry.applications) ||
    typeof entry.createdAt !== 'number' ||
    Date.now() - entry.createdAt > TTL_MS
  ) {
    clearPendingPrebuiltIntegration();
    return null;
  }

  return entry.applications;
};

/**
 * Reads the selection and removes it, so it is applied exactly once. Every
 * consumer should use this rather than `get` followed by `clear`.
 */
export const consumePendingPrebuiltIntegration = (): string[] | null => {
  const applications = getPendingPrebuiltIntegration();
  if (applications) {
    clearPendingPrebuiltIntegration();
  }
  return applications;
};
